import type { HeydayKind } from "./heyday.ts";

/**
 * HeyDex blocks: what an agent writes when an answer should show part of
 * HeyDay's record as a view instead of prose. A block is a fenced code block whose
 * language is `heydex`, names one view, and lists the records the view is drawn
 * from, one to a line:
 *
 *     ```heydex timeline
 *     title: The week, in order
 *     https://outlet.example/a#heyday-claim=abc
 *     claim:def
 *     ```
 *
 * A block carries no facts of its own. Every date, picture, outlet and sentence a
 * view shows is read from the record a line names, so a view cannot say what the
 * record does not. The only words of the agent's in one are its title.
 */
export const HEYDEX_BLOCK_LANGUAGE = "heydex";

/** Which records each view is drawn from, and how many it takes. */
export const HEYDEX_BLOCK_VIEWS = {
  timeline: {
    kinds: ["claim", "source", "report"],
    min: 2,
    max: 24,
  },
  disagreements: {
    kinds: ["story"],
    min: 1,
    max: 1,
  },
  first: {
    kinds: ["claim"],
    min: 1,
    max: 1,
  },
  brief: {
    kinds: ["story"],
    min: 1,
    max: 1,
  },
  video: {
    kinds: ["report", "source", "claim", "story"],
    min: 1,
    max: 1,
  },
  map: {
    kinds: ["story", "report"],
    min: 1,
    max: 1,
  },
  quote: {
    kinds: ["claim"],
    min: 1,
    max: 1,
  },
  records: {
    kinds: ["report", "story", "source", "claim", "entity", "situation", "outlet"],
    min: 1,
    max: 12,
  },
  people: {
    kinds: ["entity"],
    min: 1,
    max: 12,
  },
  promises: {
    kinds: ["entity", "situation"],
    min: 1,
    max: 1,
  },
} as const satisfies Record<
  string,
  {
    readonly kinds: ReadonlyArray<HeydayKind>;
    readonly min: number;
    readonly max: number;
  }
>;
export type HeydexBlockView = keyof typeof HEYDEX_BLOCK_VIEWS;
const isView = (value: string): value is HeydexBlockView =>
  Object.hasOwn(HEYDEX_BLOCK_VIEWS, value);

/** A record a block's line names: by its link, or as `kind:id`. */
export interface HeydexBlockRef {
  readonly kind: HeydayKind;
  readonly id: string;
  /** The link the line gave, which opens the record's own page; null for `kind:id`. */
  readonly href: string | null;
  /** The second of its video the link opens at. */
  readonly seconds: number | null;
}
export interface HeydexBlock {
  readonly view: HeydexBlockView;
  readonly title: string | null;
  readonly refs: ReadonlyArray<HeydexBlockRef>;
}
export type HeydexBlockParse =
  | { readonly ok: true; readonly block: HeydexBlock }
  | { readonly ok: false; readonly problem: string };

const KINDS = "report|claim|source|story|entity|situation|outlet";
const LINK = new RegExp(`https?://[^\\s<>()\\[\\]]*#heyday-(${KINDS})=([\\w.:-]{1,200})`, "g");
const SHORT = new RegExp(`(?:^|[\\s(\\[])(${KINDS}):([\\w.:-]{1,200})`, "g");
const seconds = (href: string) => {
  const match = /[?&]t=(\d+(?:\.\d+)?)s?(?:[&#]|$)/.exec(href);
  return match ? Number(match[1]) : null;
};

/** The records a line of text names, in the order it names them. */
export function refsIn(line: string): Array<HeydexBlockRef> {
  const found: Array<HeydexBlockRef & { readonly at: number }> = [];
  for (const match of line.matchAll(LINK))
    found.push({
      kind: match[1] as HeydayKind,
      id: match[2]!,
      href: match[0],
      seconds: seconds(match[0]),
      at: match.index,
    });
  // A link's own text can hold `kind:id` (its fragment does not): one inside a link is that link.
  const inLink = (at: number) =>
    found.some((ref) => ref.href !== null && at >= ref.at && at < ref.at + ref.href.length);
  for (const match of line.matchAll(SHORT)) {
    if (inLink(match.index)) continue;
    found.push({
      kind: match[1] as HeydayKind,
      id: match[2]!,
      href: null,
      seconds: null,
      at: match.index,
    });
  }
  return [...found].sort((a, b) => a.at - b.at).map(({ at: _at, ...ref }) => ref);
}

const names = () => Object.keys(HEYDEX_BLOCK_VIEWS).join(", ");
const listed = (kinds: ReadonlyArray<string>) =>
  kinds.length === 1 ? `a ${kinds[0]}` : `one of ${kinds.join(", ")}`;

/**
 * Reads a block from the text inside its fence and the words after its language
 * (`timeline` in "```heydex timeline"). The view may be named there or on the
 * first line. A block that cannot be drawn says why in words an agent can act on.
 */
export function parseHeydexBlock(code: string, meta?: string | null): HeydexBlockParse {
  const lines = code
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  let name = (meta ?? "").trim().split(/\s+/)[0]?.toLowerCase() ?? "";
  if (name === "" && lines[0] && /^[a-z]+:?$/i.test(lines[0]))
    name = lines.shift()!.replace(/:$/, "").toLowerCase();
  if (!isView(name))
    return {
      ok: false,
      problem:
        name === ""
          ? `A heydex block names its view first. The views are: ${names()}.`
          : `There is no heydex view called "${name}". The views are: ${names()}.`,
    };
  const rule = HEYDEX_BLOCK_VIEWS[name];
  let title: string | null = null;
  const refs: Array<HeydexBlockRef> = [];
  const seen = new Set<string>();
  for (const line of lines) {
    const titled = /^title:\s*(.+)$/i.exec(line);
    if (titled) {
      title = titled[1]!.slice(0, 160);
      continue;
    }
    for (const ref of refsIn(line)) {
      const key = `${ref.kind}:${ref.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      refs.push(ref);
    }
  }
  const allowed: ReadonlyArray<string> = rule.kinds;
  const wrong = refs.find((ref) => !allowed.includes(ref.kind));
  if (wrong)
    return {
      ok: false,
      problem: `A ${name} block is drawn from ${listed(allowed)}; ${wrong.kind}:${wrong.id} is a ${wrong.kind}.`,
    };
  if (refs.length < rule.min)
    return {
      ok: false,
      problem:
        rule.min === 1
          ? `A ${name} block needs ${listed(allowed)}, named by its ref link or as kind:id on a line of its own.`
          : `A ${name} block needs at least ${rule.min} records, each named by its ref link or as kind:id on a line of its own.`,
    };
  return { ok: true, block: { view: name, title, refs: refs.slice(0, rule.max) } };
}

const FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*([^\n`]*)\n([\s\S]*?)^ {0,3}\1[`~]*[ \t]*$/gm;

/** Every heydex block in a Markdown text, read or refused, in order. */
export function heydexBlocksIn(markdown: string): Array<HeydexBlockParse> {
  const blocks: Array<HeydexBlockParse> = [];
  for (const match of `${markdown}\n`.matchAll(FENCE)) {
    const [language = "", ...rest] = match[2]!.trim().split(/\s+/);
    if (language.toLowerCase() !== HEYDEX_BLOCK_LANGUAGE) continue;
    blocks.push(parseHeydexBlock(match[3]!, rest.join(" ")));
  }
  return blocks;
}

/** A Markdown text with its heydex blocks taken out: the prose around the views. */
export const withoutHeydexBlocks = (markdown: string): string =>
  markdown.includes(HEYDEX_BLOCK_LANGUAGE)
    ? `${markdown}\n`.replace(FENCE, (fence, _marks: string, info: string) =>
        info.trim().split(/\s+/)[0]?.toLowerCase() === HEYDEX_BLOCK_LANGUAGE ? "" : fence,
      )
    : markdown;
